import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  AUTH_MS_REDIRECT_FLAG,
  AuthApi,
  IApiError,
  isLoggedInAtom,
  isLoginInProgressAtom,
  Log,
  loginErrorsAtom,
  MS_FORCED_LOGIN_CLAIMS_REQUEST,
  MsGraphPermissions,
  normalizeErrors,
  ProfileStorage,
  updateTokensAndReconnectSocket,
} from "@ryde/shared";

import { useSimpleNavigation } from "../../navigation";

export const useMicrosoftLogin = () => {
  const { t } = useTranslation();
  const setLoginErrors = useSetRecoilState(loginErrorsAtom);
  const setLoggingInProgress = useSetRecoilState(isLoginInProgressAtom);
  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);
  const { navigateToHomeOrLoginReturnUrl } = useSimpleNavigation();
  const { instance, accounts } = useMsal();
  const [acquiredToken, setAcquiredToken] = useState(false);
  const [wasRedirected, setWasRedirected] = useState(false);

  const handleUserLogin = useEvent(
    async ({
      msAccessToken,
      msIdToken,
    }: {
      msAccessToken: string;
      msIdToken?: string;
    }): Promise<void> => {
      Log.info("[MS Login] handleUserLogin() step 1: invoked", {
        hasToken: !!msAccessToken,
        tokenLength: msAccessToken?.length,
        tokenPreview: msAccessToken
          ? `${msAccessToken.slice(0, 12)}...${msAccessToken.slice(-6)}`
          : null,
      });

      try {
        Log.info(
          "[MS Login] handleUserLogin() step 2: clearing loginErrors and setting loggingInProgress=true",
        );
        setLoginErrors([]);
        setLoggingInProgress(true);

        Log.info(
          "[MS Login] handleUserLogin() step 3: calling AuthApi.sessionLoginMS",
        );
        // The id_token is what carries auth_time after a forced login; the backend verifies it
        // against Microsoft's keys before trusting that instant (RYDE-7833).
        const profile = await AuthApi.sessionLoginMS({
          accessToken: msAccessToken,
          idToken: msIdToken,
        });

        Log.info(
          "[MS Login] handleUserLogin() step 4: AuthApi.sessionLoginMS returned",
          {
            hasProfile: !!profile,
            profile,
          },
        );

        Log.info(
          "[MS Login] handleUserLogin() step 5: calling updateTokensAndReconnectSocket with null tokens",
        );
        updateTokensAndReconnectSocket({
          accessToken: null,
          refreshToken: null,
        });

        if (profile) {
          ProfileStorage.prepareScopeForUserLogin(profile.id);

          Log.info(
            "[MS Login] handleUserLogin() step 6: profile present -> setIsLoggedIn(true) + navigateToHomeOrLoginReturnUrl()",
          );
          setIsLoggedIn(true);
          navigateToHomeOrLoginReturnUrl();
        } else {
          Log.warn(
            "[MS Login] handleUserLogin() step 6: profile is empty/null -> NOT logging in / NOT navigating home",
          );
        }
      } catch (error) {
        Log.error("[MS Login] handleUserLogin() ERROR caught:", {
          errorName: (error as Error)?.name,
          errorMessage: (error as Error)?.message,
          error,
        });
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        Log.info(
          "[MS Login] handleUserLogin() normalized errors:",
          errorsNormalized,
        );
        setLoginErrors(prevErrors => [
          ...(prevErrors ?? []),
          ...errorsNormalized,
        ]);
        Log.warnAndToast(t("errors.authenticationError"), error);
      } finally {
        Log.info(
          "[MS Login] handleUserLogin() finally: setLoggingInProgress(false)",
        );
        setLoggingInProgress(false);
      }
    },
  );

  const handleMicrosoftLogin = (options?: { forceLogin?: boolean }): void => {
    sessionStorage.setItem(AUTH_MS_REDIRECT_FLAG, "1");
    instance.loginRedirect({
      scopes: [MsGraphPermissions.USER_READ],
      // OAuth consent freshness (RYDE-7833): force real credential re-entry at Microsoft, and ask
      // for auth_time in the id_token so the backend can verify that it happened.
      ...(options?.forceLogin
        ? { prompt: "login", claims: MS_FORCED_LOGIN_CLAIMS_REQUEST }
        : {}),
    });
  };

  useEffect(() => {
    const pending = sessionStorage.getItem(AUTH_MS_REDIRECT_FLAG);

    Log.info("[MS Login] redirect-detect tick:", {
      pendingFlag: pending,
      accountsCount: accounts.length,
      href: window.location.href,
    });

    if (pending && accounts.length > 0) {
      Log.info(
        "[MS Login] redirect detected via sessionStorage flag — setting wasRedirected=true",
      );
      sessionStorage.removeItem(AUTH_MS_REDIRECT_FLAG);
      setWasRedirected(true);
    }
  }, [accounts]);

  useEffect(() => {
    const apply = async (): Promise<void> => {
      Log.info("[MS Login] apply() step 1: starting, accounts info:", {
        accountsCount: accounts.length,
        username: accounts[0]?.username,
        localAccountId: accounts[0]?.localAccountId,
        homeAccountId: accounts[0]?.homeAccountId,
        tenantId: accounts[0]?.tenantId,
        environment: accounts[0]?.environment,
      });

      const accessTokenRequest = {
        scopes: [MsGraphPermissions.USER_READ],
        account: accounts[0],
      };

      Log.info(
        "[MS Login] apply() step 2: built accessTokenRequest:",
        accessTokenRequest,
      );

      try {
        Log.info("[MS Login] apply() step 3: setting acquiredToken=true");
        setAcquiredToken(true);

        Log.info(
          "[MS Login] apply() step 4: calling instance.acquireTokenSilent",
        );
        const accessTokenResponse =
          await instance.acquireTokenSilent(accessTokenRequest);
        // Acquire token silent success
        Log.info("[MS Login] apply() step 5: acquireTokenSilent SUCCESS:", {
          tokenType: accessTokenResponse.tokenType,
          scopes: accessTokenResponse.scopes,
          expiresOn: accessTokenResponse.expiresOn,
          extExpiresOn: accessTokenResponse.extExpiresOn,
          fromCache: accessTokenResponse.fromCache,
          accessTokenLength: accessTokenResponse.accessToken?.length,
          accessTokenPreview: accessTokenResponse.accessToken
            ? `${accessTokenResponse.accessToken.slice(0, 12)}...${accessTokenResponse.accessToken.slice(-6)}`
            : null,
          account: {
            username: accessTokenResponse.account?.username,
            tenantId: accessTokenResponse.account?.tenantId,
          },
          uniqueId: accessTokenResponse.uniqueId,
          authority: accessTokenResponse.authority,
        });

        Log.info(
          "[MS Login] apply() step 6: calling handleUserLogin with MS access token",
        );
        await handleUserLogin({
          msAccessToken: accessTokenResponse.accessToken,
          msIdToken: accessTokenResponse.idToken,
        });

        Log.info(
          "[MS Login] apply() step 7: handleUserLogin finished successfully",
        );
        Log.debug(
          `Logged in with Microsoft credentials for user: ${accounts[0]?.username}`,
        );
      } catch (error) {
        Log.error("[MS Login] apply() step ERROR caught:", {
          errorName: (error as Error)?.name,
          errorMessage: (error as Error)?.message,
          isInteractionRequired: error instanceof InteractionRequiredAuthError,
          error,
        });

        if (error instanceof InteractionRequiredAuthError) {
          Log.info(
            "[MS Login] apply() InteractionRequired: calling acquireTokenRedirect",
          );
          try {
            await instance.acquireTokenRedirect(accessTokenRequest);
          } catch (newError) {
            /* Acquire token interactive failure */
            /* TODO handle bad scenarios */
            /* Browser blocking Popup */
            /* Access token scopes aren't guaranteed */
            Log.error(
              t("errors.loggedInWithMicrosoftCredentialsFailed", {
                username: accounts[0]?.username || "-",
              }),
              newError,
            );
          }
        }

        Log.error(
          t("errors.unhandledSilentAcquireTokenError", {
            username: accounts[0]?.username || "-",
          }),
          error,
        );
      }
    };

    const willApply = !!(accounts.length && wasRedirected && !acquiredToken);

    Log.info("[MS Login] apply useEffect tick — guard evaluation:", {
      accountsLength: accounts.length,
      hasAccount: accounts.length > 0,
      wasRedirected,
      acquiredToken,
      willApply,
      blockedBy: !willApply
        ? {
            noAccounts: accounts.length === 0,
            notRedirected: !wasRedirected,
            alreadyAcquired: acquiredToken,
          }
        : null,
    });

    if (accounts.length && wasRedirected && !acquiredToken) {
      Log.info(
        "[MS Login] apply useEffect: guard passed — INVOKING apply() now",
      );
      apply();
    } else {
      Log.info(
        "[MS Login] apply useEffect: guard FAILED — apply() NOT invoked",
      );
    }
  }, [accounts, acquiredToken, handleUserLogin, instance, t, wasRedirected]);

  return {
    handleMicrosoftLogin,
  };
};
